import { useEffect, useMemo, useState } from 'react';

import {
  fetchAuthenticatedUser,
  hydrateAuthenticatedUser,
  useAppConfig,
  getSiteConfig,
  sendPageEvent, sendTrackEvent,
  useIntl,
} from '@openedx/frontend-base';
import { Form, Spinner, StatefulButton } from '@openedx/paragon';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import Skeleton from 'react-loading-skeleton';

import ConfigurableRegistrationForm from './components/ConfigurableRegistrationForm';
import { useRegisterContext } from './components/RegisterContext';
import RegistrationFailure from './components/RegistrationFailure';
import { useFieldValidations, useRegistration } from './data/apiHook';
import {
  FORM_SUBMISSION_ERROR,
  TPA_AUTHENTICATION_FAILURE,
} from './data/constants';
import {
  isFormValid, prepareRegistrationPayload,
} from './data/utils';
import messages from './messages';
import { EmailField, NameField, UsernameField } from './RegistrationFields';
import {
  InstitutionLogistration,
  PasswordField,
  RedirectLogistration,
  ThirdPartyAuthAlert,
} from '../common-components';
import { useThirdPartyAuthContext } from '../common-components/components/ThirdPartyAuthContext';
import { useThirdPartyAuthHook } from '../common-components/data/apiHook';
import EnterpriseSSO from '../common-components/EnterpriseSSO';
import ThirdPartyAuth from '../common-components/ThirdPartyAuth';
import { registerPath } from '../constants';
import { COMPLETE_STATE, DEFAULT_STATE, PENDING_STATE } from '../data/constants';
import {
  getAllPossibleQueryParams, getTpaHint, getTpaProvider, isHostAvailableInQueryParams, setCookie,
} from '../data/utils';

/**
 * Inner Registration Page component that uses the context
 */
const RegistrationPage = (props) => {
  const { formatMessage } = useIntl();
  const {
    fieldDescriptions,
    optionalFields,
    thirdPartyAuthApiStatus,
    thirdPartyAuthContext,
    setThirdPartyAuthContextBegin,
    setThirdPartyAuthContextSuccess,
    setThirdPartyAuthContextFailure,
  } = useThirdPartyAuthContext();

  const {
    autoSubmitRegForm,
    currentProvider,
    finishAuthUrl,
    pipelineUserDetails,
    providers,
    secondaryProviders,
    errorMessage: thirdPartyAuthErrorMessage,
  } = thirdPartyAuthContext;

  const {
    clearRegistrationBackendError,
    registrationFormData,
    registrationResult,
    registrationError,
    setEmailSuggestionContext,
    updateRegistrationFormData,
    setRegistrationError,
    setRegistrationResult,
    setValidationsSuccess,
    setValidationsFailure,
    validationApiRateLimited,
    backendValidations,
    setBackendCountryCode,
  } = useRegisterContext();

  const fieldValidationsMutation = useFieldValidations({
    onSuccess: (data) => { setValidationsSuccess(data); },
    onError: () => { setValidationsFailure(); },
  });

  const registrationEmbedded = isHostAvailableInQueryParams();
  const platformName = getSiteConfig().siteName;
  const {
    handleInstitutionLogin,
    institutionLogin,
  } = props;
  const {
    ENABLE_AUTO_GENERATED_USERNAME,
    ENABLE_PROGRESSIVE_PROFILING_ON_AUTHN,
    MARKETING_EMAILS_OPT_IN,
    USER_RETENTION_COOKIE_NAME,
    SESSION_COOKIE_DOMAIN,
  } = useAppConfig();
  const flags = {
    showMarketingEmailOptInCheckbox: MARKETING_EMAILS_OPT_IN,
    autoGeneratedUsernameEnabled: ENABLE_AUTO_GENERATED_USERNAME,
  };

  const backendRegistrationError = registrationError;
  const registrationMutation = useRegistration({
    onSuccess: async (data) => {
      const redirectUrl = localNextPath || data.redirectUrl || '';
      if (redirectUrl.startsWith('/')) {
        await fetchAuthenticatedUser({ forceRefresh: true });
        // Hydrate in the background — publishes AUTHENTICATED_USER_CHANGED after
        // SPA navigation, so the header picks up the full user profile (avatar, etc.)
        hydrateAuthenticatedUser();
      }
      setRegistrationResult({ ...data, redirectUrl });
      setRegistrationError({});
    },
    onError: (errorData) => {
      setRegistrationError(errorData);
    },
  });

  const [userPipelineDataLoaded, setUserPipelineDataLoaded] = useState(false);
  const registrationErrorCode = registrationError?.errorCode || backendRegistrationError?.errorCode;
  const submitState = registrationMutation.isPending ? PENDING_STATE : DEFAULT_STATE;
  const queryParams = useMemo(() => getAllPossibleQueryParams(), []);
  const localNextPath = queryParams.next?.startsWith('/') ? queryParams.next : null;
  const tpaHint = useMemo(() => getTpaHint(), []);
  // Initialize form state from local backedUpFormData
  const backedUpFormData = registrationFormData;
  const [formFields, setFormFields] = useState({ ...backedUpFormData.formFields });
  const [configurableFormFields, setConfigurableFormFields] = useState({ ...backedUpFormData.configurableFormFields });
  const [errors, setErrors] = useState({ ...backedUpFormData.errors });
  const [errorCode, setErrorCode] = useState({ type: '', count: 0 });
  const [formStartTime, setFormStartTime] = useState(null);

  // Redirect to login if a user hits the page directly without an active TPA context
  useEffect(() => {
    const currentQueries = getAllPossibleQueryParams();
    const hasUrlHint = !!(currentQueries.tpa_hint || currentQueries.provider || currentQueries.auth_entry);
    const hasSessionHint = !!getTpaHint();
    const hasLoadedTpaPayload = !!(currentProvider || (pipelineUserDetails && Object.keys(pipelineUserDetails).length > 0));

    // 1. If we already have explicit data or hooks loaded, stay on the page
    if (hasUrlHint || hasSessionHint || hasLoadedTpaPayload) {
      return;
    }

    // 2. Detect if the user's browser is arriving from an external provider (like CILogon)
    const referrer = document.referrer || '';
    const isComingFromTpa = referrer.includes('cilogon.org') || (referrer && !referrer.includes(window.location.hostname));

    // 3. INSTANT REDIRECT: If they did NOT come from an external provider,
    // they are a direct visitor. Kick them to login immediately (0ms delay).
    //if (!isComingFromTpa) {
    //  window.location.href = '/authn/login';
    //  return;
    //}

    // 4. SAFE BUFFER FOR CILOGON: If they did come from your provider, give the 
    // network a generous 1500ms to resolve the background /api/mfe_context pipeline.
    const tpaValidationTimeout = setTimeout(() => {
      const freshQueries = getAllPossibleQueryParams();
      const verifiedContext = !!(
        currentProvider || 
        getTpaHint() || 
        freshQueries.tpa_hint || 
        (pipelineUserDetails && Object.keys(pipelineUserDetails).length > 0)
      );

      if (!verifiedContext) {
        window.location.href = '/authn/login';
      }
    }, 1500);

    return () => clearTimeout(tpaValidationTimeout);
  }, [currentProvider, pipelineUserDetails, thirdPartyAuthApiStatus]);

  // temporary error state for embedded experience because we don't want to show errors on blur
  const [temporaryErrors, setTemporaryErrors] = useState({ ...backedUpFormData.errors });
  const { cta, host } = queryParams;
  const buttonLabel = cta
    ? formatMessage(messages['create.account.cta.button'], { label: cta })
    : formatMessage(messages['create.account.for.free.button']);

  /**
   * Set the userPipelineDetails data in formFields for only first time
   */
  useEffect(() => {
    if (!userPipelineDataLoaded && thirdPartyAuthApiStatus === COMPLETE_STATE) {
      if (thirdPartyAuthErrorMessage) {
        setErrorCode(prevState => ({ type: TPA_AUTHENTICATION_FAILURE, count: prevState.count + 1 }));
      }
      if (pipelineUserDetails && Object.keys(pipelineUserDetails).length !== 0) {
	pipelineUserDetails.username = '';
        const { name = '', username = '', email = '' } = pipelineUserDetails;
        setFormFields(prevState => ({
          ...prevState, name, username, email,
        }));
        setUserPipelineDataLoaded(true);
      }
    }
  }, [
    thirdPartyAuthApiStatus,
    thirdPartyAuthErrorMessage,
    pipelineUserDetails,
    userPipelineDataLoaded,
  ]);

  const params = { ...queryParams, is_register_page: true };
  if (tpaHint) {
    params.tpa_hint = tpaHint;
  }
  const { data, isSuccess, error } = useThirdPartyAuthHook(registerPath, params);
  useEffect(() => {
    if (!formStartTime) {
      sendPageEvent('login_and_registration', 'register');
      setThirdPartyAuthContextBegin();
      setFormStartTime(Date.now());
    }
    if (formStartTime) {
      if (isSuccess && data) {
        setThirdPartyAuthContextSuccess(
          data.fieldDescriptions,
          data.optionalFields,
          data.thirdPartyAuthContext,
        );
        setBackendCountryCode(data.thirdPartyAuthContext.countryCode);
      }

      if (error) {
        setThirdPartyAuthContextFailure();
      }
    }
  }, [formStartTime, isSuccess, data, error,
    setThirdPartyAuthContextBegin, setThirdPartyAuthContextSuccess,
    setBackendCountryCode, setThirdPartyAuthContextFailure]);

  // Handle backend validation errors from context
  useEffect(() => {
    if (backendValidations) {
      if (registrationEmbedded) {
        setTemporaryErrors(prevErrors => ({ ...prevErrors, ...backendValidations }));
      } else {
        setErrors(prevErrors => ({ ...prevErrors, ...backendValidations }));
      }
    }
  }, [backendValidations, registrationEmbedded]);

  useEffect(() => {
    if (registrationErrorCode) {
      setErrorCode(prevState => ({ type: registrationErrorCode, count: prevState.count + 1 }));
    }
  }, [registrationErrorCode]);

  useEffect(() => {
    if (registrationResult.success) {
      // This event is used by GTM
      sendTrackEvent('edx.bi.user.account.registered.client', {});

      // This is used by the "User Retention Rate Event" on GTM
      setCookie(USER_RETENTION_COOKIE_NAME, true, SESSION_COOKIE_DOMAIN);
    }
  }, [registrationResult, USER_RETENTION_COOKIE_NAME, SESSION_COOKIE_DOMAIN]);

  const handleOnChange = (event) => {
    const { name } = event.target;
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (backendRegistrationError[name]) {
      clearRegistrationBackendError(name);
    }
    // Clear context registration errors
    if (registrationError.errorCode) {
      setRegistrationError({});
    }
    setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
    // Update local state
    const newFormFields = { ...formFields, [name]: value };
    setFormFields(newFormFields);
    // Save to context for persistence across tab switches
    updateRegistrationFormData({
      formFields: newFormFields,
      errors,
      configurableFormFields,
    });
  };

  const handleErrorChange = (fieldName, errorMessage) => {
    if (registrationEmbedded) {
      setTemporaryErrors(prevErrors => ({
        ...prevErrors,
        [fieldName]: errorMessage,
      }));
      if (errorMessage === '' && errors[fieldName] !== '') {
        setErrors(prevErrors => ({
          ...prevErrors,
          [fieldName]: errorMessage,
        }));
      }
    } else {
      setErrors(prevErrors => ({
        ...prevErrors,
        [fieldName]: errorMessage,
      }));
    }
  };

  const registerUser = () => {
    const totalRegistrationTime = (Date.now() - formStartTime) / 1000;
    let payload = { ...formFields };

    if (currentProvider) {
      delete payload.password;
      payload.social_auth_provider = currentProvider;
    }
    if (flags.autoGeneratedUsernameEnabled) {
      delete payload.username;
    }

    // Validating form data before submitting
    const { isValid, fieldErrors, emailSuggestion } = isFormValid(
      payload,
      registrationEmbedded ? temporaryErrors : errors,
      configurableFormFields,
      fieldDescriptions,
      formatMessage,
    );
    setErrors({ ...fieldErrors });
    updateRegistrationFormData({
      formFields,
      errors: fieldErrors,
      configurableFormFields,
    });
    setEmailSuggestionContext(emailSuggestion.suggestion, emailSuggestion.type);

    // returning if not valid
    if (!isValid) {
      setErrorCode(prevState => ({ type: FORM_SUBMISSION_ERROR, count: prevState.count + 1 }));
      return;
    }

    // Preparing payload for submission
    payload = prepareRegistrationPayload(
      payload,
      configurableFormFields,
      flags.showMarketingEmailOptInCheckbox,
      totalRegistrationTime,
      queryParams,
    );
    // making register call with React Query
    registrationMutation.mutate(payload);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    registerUser();
  };

  useEffect(() => {
    if (autoSubmitRegForm && userPipelineDataLoaded) {
      registerUser();
    }
  }, [autoSubmitRegForm, userPipelineDataLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderForm = () => {
    if (institutionLogin) {
      return (
        <InstitutionLogistration
          secondaryProviders={secondaryProviders}
          headingTitle={formatMessage(messages['register.institution.login.page.title'])}
        />
      );
    }
    return (
      <>
        <Helmet>
          <title>{formatMessage(messages['register.page.title'], { siteName: getSiteConfig().siteName })}</title>
        </Helmet>
        <RedirectLogistration
          host={host}
          authenticatedUser={registrationResult.authenticatedUser}
          success={registrationResult.success}
          redirectUrl={registrationResult.redirectUrl}
          finishAuthUrl={finishAuthUrl}
          optionalFields={optionalFields}
          registrationEmbedded={registrationEmbedded}
          redirectToProgressiveProfilingPage={
            ENABLE_PROGRESSIVE_PROFILING_ON_AUTHN && !!Object.keys(optionalFields.fields).length
          }
        />
        {autoSubmitRegForm && !errorCode.type ? (
          <div className="mw-xs mt-5 text-center">
            <Spinner animation="border" variant="primary" id="tpa-spinner" />
          </div>
        ) : (
          <div
            className={classNames(
              'mw-xs mt-3',
              { 'w-100 m-auto pt-4 main-content': registrationEmbedded },
            )}
          >
            <ThirdPartyAuthAlert
              currentProvider={currentProvider}
              platformName={platformName}
              referrer={registerPath}
            />
            <RegistrationFailure
              errorCode={errorCode.type}
              failureCount={errorCode.count}
              context={{ provider: currentProvider, errorMessage: thirdPartyAuthErrorMessage }}
            />
            <Form id="registration-form" name="registration-form">
              <NameField
                name="name"
                value={formFields.name}
                shouldFetchUsernameSuggestions={!formFields.username.trim()}
                handleChange={handleOnChange}
                handleErrorChange={handleErrorChange}
                errorMessage={errors.name}
                helpText={[formatMessage(messages['help.text.name'])]}
                floatingLabel={formatMessage(messages['registration.fullname.label'])}
              />
              <EmailField
                name="email"
                value={formFields.email}
                confirmEmailValue={configurableFormFields?.confirm_email}
                handleErrorChange={handleErrorChange}
                handleChange={handleOnChange}
                errorMessage={errors.email}
                helpText={[formatMessage(messages['help.text.email'])]}
                floatingLabel={formatMessage(messages['registration.email.label'])}
		readOnly="true"
              />
              {!flags.autoGeneratedUsernameEnabled && (
                <UsernameField
                  name="username"
                  spellCheck="false"
                  value={formFields.username}
                  handleChange={handleOnChange}
                  handleErrorChange={handleErrorChange}
                  errorMessage={errors.username}
                  helpText={[formatMessage(messages['help.text.username.1']), formatMessage(messages['help.text.username.2'])]}
                  floatingLabel={formatMessage(messages['registration.username.label'])}
                />
              )}
              {!currentProvider && (
                <PasswordField
                  name="password"
                  value={formFields.password}
                  handleChange={handleOnChange}
                  handleErrorChange={handleErrorChange}
                  errorMessage={errors.password}
                  floatingLabel={formatMessage(messages['registration.password.label'])}
                  clearRegistrationBackendError={clearRegistrationBackendError}
                  validateField={fieldValidationsMutation.mutate}
                  validationApiRateLimited={validationApiRateLimited}
                />
              )}
              <ConfigurableRegistrationForm
                email={formFields.email}
                fieldErrors={errors}
                formFields={configurableFormFields}
                setFieldErrors={registrationEmbedded ? setTemporaryErrors : setErrors}
                setFormFields={setConfigurableFormFields}
                autoSubmitRegisterForm={autoSubmitRegForm}
                fieldDescriptions={fieldDescriptions}
              />
              <StatefulButton
                id="register-user"
                name="register-user"
                type="submit"
                variant="brand"
                className="register-button mt-4 mb-4"
                state={submitState}
                labels={{
                  default: buttonLabel,
                  pending: '',
                }}
                onClick={handleSubmit}
                onMouseDown={(e) => e.preventDefault()}
              />
              {!registrationEmbedded && (
                <ThirdPartyAuth
                  currentProvider={currentProvider}
                  providers={providers}
                  secondaryProviders={secondaryProviders}
                  handleInstitutionLogin={handleInstitutionLogin}
                  thirdPartyAuthApiStatus={thirdPartyAuthApiStatus}
                />
              )}
            </Form>
          </div>
        )}
      </>
    );
  };

  if (tpaHint) {
    if (thirdPartyAuthApiStatus === PENDING_STATE) {
      return <Skeleton height={36} />;
    }
    const { provider, skipHintedLogin } = getTpaProvider(tpaHint, providers, secondaryProviders);
    if (skipHintedLogin) {
      window.location.href = getSiteConfig().lmsBaseUrl + provider.registerUrl;
      return null;
    }
    return provider ? <EnterpriseSSO provider={provider} /> : renderForm();
  }
  return (
    renderForm()
  );
};

RegistrationPage.propTypes = {
  institutionLogin: PropTypes.bool,
  handleInstitutionLogin: PropTypes.func,
};

RegistrationPage.defaultProps = {
  handleInstitutionLogin: null,
  institutionLogin: false,
};

export default RegistrationPage;
